import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Mails, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "ppw_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": t };
      const [q, s] = await Promise.all([
        axios.get(`${API}/quotes`, { headers }),
        axios.get(`${API}/stats`, { headers }),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Failed to load quotes.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back!");
    } catch {
      toast.error("Invalid passcode.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPasscode("");
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}?status_val=${status}`, {}, { headers: { "X-Admin-Token": token } });
      load(token);
    } catch {
      toast.error("Update failed.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#18181B] flex items-center justify-center px-5">
        <form onSubmit={login} data-testid="admin-login-form"
          className="bg-white border border-zinc-200 p-10 w-full max-w-md">
          <div className="w-12 h-12 bg-primary text-white flex items-center justify-center mb-6"><Lock className="w-6 h-6" /></div>
          <h1 className="font-display font-extrabold text-2xl text-zinc-900">Admin Access</h1>
          <p className="text-zinc-500 text-sm mt-2 mb-6">Enter your passcode to view quote requests.</p>
          <Label className="overline text-zinc-500">Passcode</Label>
          <Input type="password" data-testid="admin-passcode-input" value={passcode}
            onChange={(e) => setPasscode(e.target.value)} className="rounded-none h-12 mt-2 bg-zinc-50 border-zinc-300 focus-visible:ring-accent" />
          <Button type="submit" data-testid="admin-login-btn"
            className="rounded-none w-full h-12 mt-6 bg-accent hover:bg-accent/90 text-white font-semibold">
            Sign In
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="logo" className="h-10" />
            <span className="font-display font-extrabold text-lg tracking-tight">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => load(token)} data-testid="admin-refresh-btn" className="rounded-none">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn" className="rounded-none">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-zinc-200 p-6 flex items-center gap-5" data-testid="stat-total">
            <span className="w-12 h-12 bg-primary text-white flex items-center justify-center"><Mails className="w-6 h-6" /></span>
            <div><div className="font-display font-extrabold text-3xl">{stats.total}</div><div className="overline text-zinc-500">Total Requests</div></div>
          </div>
          <div className="bg-white border border-zinc-200 p-6 flex items-center gap-5" data-testid="stat-new">
            <span className="w-12 h-12 bg-accent text-white flex items-center justify-center"><Inbox className="w-6 h-6" /></span>
            <div><div className="font-display font-extrabold text-3xl">{stats.new}</div><div className="overline text-zinc-500">New / Unread</div></div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200">
          <Table data-testid="quotes-table">
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-16 text-zinc-500">No quote requests yet.</TableCell></TableRow>
              ) : quotes.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-semibold">{q.name}</TableCell>
                  <TableCell className="text-zinc-600">{q.company || "-"}</TableCell>
                  <TableCell className="text-sm">
                    <div>{q.phone}</div>
                    <div className="text-zinc-500">{q.email}</div>
                  </TableCell>
                  <TableCell className="text-sm">{q.service}</TableCell>
                  <TableCell className="max-w-[220px] text-sm text-zinc-600 truncate" title={q.message}>{q.message || "-"}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-none ${q.status === "new" ? "bg-accent" : "bg-primary"}`}>{q.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {q.status === "new" ? (
                      <Button size="sm" variant="outline" className="rounded-none" data-testid={`mark-contacted-${q.id}`}
                        onClick={() => setStatus(q.id, "contacted")}>Mark Contacted</Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="rounded-none" data-testid={`mark-new-${q.id}`}
                        onClick={() => setStatus(q.id, "new")}>Reopen</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
